import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  ExistingProvider,
  forwardRef,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChange,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {takeUntil} from 'rxjs/operators';
import {destroy$} from '../destroyable';
import {DropdownMenuItem} from '../dropdown-menu/dropdown-menu-item';
import {DynamicComponentRef} from '../dynamic-component';
import {ModalService} from '../modal';
import {MultiselectDropdownMenuComponent} from './multiselect-dropdown-menu';

export interface MultiselectConfig<T = any> {
    menuItems: Array<DropdownMenuItem<T>>;
    noneMenuItem?: DropdownMenuItem<T>;
    label: string;

    /**
     * Disables multi selection, allowed only one of these variants:
     * - All/None: if present {@link noneMenuItem}, then only None, if not present, then only All.
     * - one of options.
     */
    isSingleSelectionMode?: boolean;
}

export interface MultiselectInputChanges extends SimpleChanges {
    config: SimpleChange;
}

const MULTISELECT_CONTROL_VALUE_ACCESSOR: ExistingProvider = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MultiselectComponent),
    multi: true
};

@Component({
    selector: 'multiselect',
    templateUrl: './multiselect.component.html',
    styleUrls: ['./multiselect.component.scss'],
    providers: [MULTISELECT_CONTROL_VALUE_ACCESSOR],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MultiselectComponent implements ControlValueAccessor, OnInit, OnChanges {

    @Input()
    public config: MultiselectConfig;

    @ViewChild('dropdownMenuTarget', {static: true})
    public dropdownMenuTarget: ElementRef;

    @HostBinding('class.-is-open')
    public dropdownMenu: DynamicComponentRef<MultiselectDropdownMenuComponent>;

    public isNoSelection: boolean = true;

    public checkedVariantsLength: number;

    public checkedVariantLabel: string;

    public readonly destroy$ = destroy$();

    private innerValue: any[];

    constructor(
        private modalService: ModalService,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    /**
     * This callback needed for successful init of lang decorator.
     * console.log is needed for creating ngOnInit on build.
     * (won't be created if empty)
     */
    public ngOnInit(): void {
        console.log('Init Report Details Widget');
    }

    public ngOnChanges(changes: MultiselectInputChanges): void {
        if (changes.config) {
            this.setConfigByValue(this.innerValue);
        }
    }

    public writeValue(value: any[]): void {
        this.innerValue = value;
        this.setConfigByValue(value);
        this.changeDetectorRef.markForCheck();
    }

    public propagateChange(value: any): void {
        // To be overridden by parent via registerOnChange.
    }

    public registerOnChange(handler: (value: any) => void): void {
        this.propagateChange = handler;
    }

    public registerOnTouched(handler: () => void): void {
        // Not implemented.
    }

    @HostListener('click')
    public openDropdown(): void {
        if (!this.dropdownMenu) {
            this.dropdownMenu = this.modalService.open(MultiselectDropdownMenuComponent, {
                configure: {
                    targetElement:         this.dropdownMenuTarget,
                    menuItemsConfig:       this.config.menuItems,
                    noneMenuItem:          this.config.noneMenuItem,
                    checkedVariantsConfig: this.innerValue,
                    isSingleSelectionMode: this.config.isSingleSelectionMode || false,
                    onCloseCallback:       () => {
                        this.dropdownMenu = null;
                        this.changeDetectorRef.markForCheck();
                    }
                }
            });

            this.dropdownMenu
                .instance
                .checkedVariantsChange$.pipe(
                    takeUntil(this.destroy$)
                )
                .subscribe((checkedVariants: any[]) => {
                    this.writeValue(checkedVariants);
                    this.propagateChange(checkedVariants);
                });
        }
    }

    protected setConfigByValue(value: any[]): void {
        const valueLength = value && value.length || 0;
        this.checkedVariantsLength = valueLength;

        const noneItem = this.config.noneMenuItem;
        const isNoneItemSelected = !valueLength && noneItem;

        if (isNoneItemSelected) {
            this.checkedVariantLabel = noneItem.label;
        } else {
            const checkedItem = (valueLength === 1)
                ? this.config && this.config.menuItems.find(item => item.value === value[0])
                : undefined;

            this.checkedVariantLabel = checkedItem && checkedItem.label;
        }

        this.isNoSelection = !(valueLength && valueLength !== this.config.menuItems.length);
    }

}
